import React, { useState, useEffect, useMemo, useRef } from 'react';
import transformerFunctions from '../../store/transformer/functions';
import { IRootState } from '../../@types/redux';
import { connect } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import Icon from '@material-ui/core/Icon';
import Toolbar from '@material-ui/core/Toolbar';
import Card from '@material-ui/core/Card';
import MaterialTable from '../customComponents/materialTable/Table';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import lodash from 'lodash';
import TransformerInfo from './Info';
import navFunctions from '../../store/nav/functions';
import TransformerNewButton from './button/NewButton';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import appFunctionsService from '../../services/appFunctionsService';
import { ITransformer, TransformerMeterTypeUpperCase } from '../../@types/model/transformer/transformer';
import Fab from '@material-ui/core/Fab';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import MapIcon from '@material-ui/icons/Map';
import TransformerFilterSelector from '../customComponents/selector/transformer/FilterSelector';
import { IUserSession } from '../../@types/employee';
import { CSVLinkProps } from '../../@types/csv';
import DeleteIcon from '@material-ui/icons/Delete';
import Screen from '../customComponentV2/screen/Screen';
import ConfirmDialog from '../customComponents/dialog/ConfirmDialog';
import ConfirmationDialog from '../customComponentV2/dialog/ConfirmationDialog';

interface ITransformerListProps {
    transformers : Array<ITransformer>;
    isLoadingTransformers : boolean;
    session ?: IUserSession | null;
}

const TransformerList = (props : ITransformerListProps) : React.ReactElement => {
    const [sortList, setSortList] = useState<Array<{ field : string; direction : 'asc' | 'desc' }>>([]);
    const [showAll, setShowAll] = useState<boolean>(false);
    const [epNumber, setEpNumber] = useState<string>('');
    const [epGroup, setEpGroup] = useState<string>('');
    const [epArea, setEpArea] = useState<string>('');
    const [type, setType] = useState<string>('');
    const [meterType, setMeterType] = useState<TransformerMeterTypeUpperCase | null>(null);
    const [transformerToDelete, setTransformerToDelete] = useState<ITransformer | null>(null);

    const csvLink = useRef<CSVLinkProps & HTMLAnchorElement>(null);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    useEffect(() : void => {
        transformerFunctions.getList();
    }, []);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onSortChanged = (newSortList : Array<{ field : string; direction : 'asc' | 'desc' }>) : void => {
        setSortList(newSortList);
    };

    const onInfoClick = (selectedRow : ITransformer) : void => {
        transformerFunctions.setSelected(selectedRow);
    };

    const onMapClick = (selectedRow : ITransformer) : void => {
        navFunctions.navTransformerMap(selectedRow.EPNumber);
    };

    const onCSVClick = () : void => {
        if (csvLink.current) {
            csvLink.current.link.click();
        }
    };

    const onShowOnlyActiveClick = () : void => {
        setShowAll(prevShowAll => !prevShowAll);
    };

    const onChange = (newEpNumber : string | null, newEpGroup : string | null, newEpArea : string | null, newType : string | null, newMeterType : TransformerMeterTypeUpperCase | null) : void => {
        setEpNumber(newEpNumber ?? '');
        setEpGroup(newEpGroup ?? '');
        setEpArea(newEpArea ?? '');
        setType(newType ?? '');
        setMeterType(newMeterType);
    };

    const deleteTransformer = (selectedRow : ITransformer) : void => {
        setTransformerToDelete(selectedRow);
    };

    const handleDelete = () : void => {
        if (!transformerToDelete) return;

        transformerFunctions.deactivate(transformerToDelete.EPNumber);

        setTransformerToDelete(null);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const filteredData = useMemo<Array<ITransformer>>(() => {
        let sortedList = lodash
            .chain(props.transformers)
            .filter(n => showAll || !!n.IsActive)
            .filter(n => n.EPNumber.includes(epNumber))
            .filter(n => n.Division.toLowerCase().includes(epArea.toLowerCase()))
            .filter(n => !epGroup || !!n.EPGroup?.includes(epGroup))
            .filter(n => !type || !!n.EPType?.toLowerCase().includes(type.toLowerCase()))
            .filter(n => !meterType || n.MeterType === meterType)
            .value();

        if (sortList.length === 0) {
            return sortedList.sort((a, b) => parseInt(a.EPNumber.substr(2)) - parseInt(b.EPNumber.substr(2)));
        }

        sortList
            .forEach((n) => {
                if (n.field === 'EPNumber') {
                    sortedList = sortedList.sort((a, b) => parseInt(b.EPNumber.substr(2)) - parseInt(a.EPNumber.substr(2)) * (n.direction === 'desc' ? 1 : -1));
                } else {
                    sortedList = lodash.orderBy(sortedList, n.field, n.direction);
                }
            });

        return sortedList;
    }, [props.transformers, sortList, showAll, epNumber, epArea, epGroup, type, meterType]);

    const csvData = useMemo<Array<Record<string, any>>>(() => {
        return filteredData.filter(n => n.IsActive || typeof n.IsActive === 'undefined').map(n => ({
            EPNumber: n.EPNumber,
            EPGroup: n.EPGroup,
            PoleNumber: n.PoleNumber,
            Division: n.Division,
            SubArea: n.SubArea,
            Latitude: !n.Geom ? 0 : n.Geom.latitude,
            Longitude: !n.Geom ? 0 : n.Geom.longitude,
            CNC: n.CNC,
            EPType: n.EPType,
            AccountNumber: n.AccountNumber,
            PremiseId: n.PremiseId,
            Line: n.Line,
            LineVoltage: n.LineVoltage,
            MeterType: n.MeterType,
            CircuitBreaker: n.CircuitBreaker,
            TransformerRating: n.TransformerRating,
            MeterNumbers: !n.TransformerMeterNumbers ? '' : n.TransformerMeterNumbers.map(x => x.Number).join(', '),
            CreatedOn: appFunctionsService.formatDateTime(n.CreatedOn),
            UpdatedOn: appFunctionsService.formatDateTime(n.UpdatedOn),
            CurrentResident: n.CurrentResident,
            SecurityGuarantee: n.SecurityGuarantee ?? '',
            SecurityDeposit: n.SecurityDeposit ?? '',
            DefaultReader: !n.TransformerReaderEmployeeNr || !n.TransformerReaderEmployeeName ?
                '' :
                `${n.TransformerReaderEmployeeNr} - ${n.TransformerReaderEmployeeName}`,
        }));
    }, [filteredData]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen
            useAnimatedLoader
            isLoading={props.isLoadingTransformers}
            isPadded={false}>
            <div className={'fdc flx1 p10 mh0 mw0'}>
                <div className={'flx1 fdc mh0 mw0'}>
                    <Toolbar>
                        <Typography variant='h5' color='inherit'>
                            TRANSFORMERS
                        </Typography>
                        {
                            props.session?.employee.IsTransformerAdmin &&
                            <TransformerNewButton />
                        }
                        <span className={'flx4'} />
                        <div className='flx1'>
                            <TransformerFilterSelector
                                id='transformer_filter'
                                onChange={onChange}
                                epNumber={epNumber}
                                epArea={epArea}
                                epGroup={epGroup}
                                type={type}
                                meterType={meterType}
                                label='Filter By...'
                                disabled={props.isLoadingTransformers}
                            />
                        </div>
                        <div className={'fdr pt5 jcfe aic'}>
                            <div style={{ paddingRight: '15px' }}>
                                <Tooltip title={showAll ? 'Show only Active' : 'Show all'}>
                                    <Fab color={!showAll ? 'primary' : 'default'} size='small' onClick={onShowOnlyActiveClick}>
                                        <Icon>
                                            <img src={`${ASSET_BASE}/assets/images/icons/active.svg`} />
                                        </Icon>
                                    </Fab>
                                </Tooltip>
                            </div>
                            <div className={'fdr pt5'}>
                                <span style={{ paddingRight: '15px' }}><b>Total: </b>{filteredData.length}</span>
                            </div>
                        </div>
                    </Toolbar>
                    <Card className={'flx1 fdc'}>
                        <MaterialTable<ITransformer>
                            id='transformersTable'
                            data={filteredData}
                            rowsPerPage={100}
                            externalSort
                            onSortChange={onSortChanged}
                            onExportCsvClick={!props.session?.employee.IsTransformerExport ? undefined : onCSVClick}
                            columns={[{
                                header: 'EP Nr',
                                field: 'EPNumber',
                                width: 200,
                                enableSort: true,
                                enableFilter: false,
                                paddingRight: 4,
                                fixed: true,
                                renderCell: row => (
                                    <div className={'aic'}>
                                        <Tooltip title={'Info'}>
                                            <div>
                                                <IconButton color={'primary'} onClick={() => onInfoClick(row)}>
                                                    <InfoOutlinedIcon />
                                                </IconButton>
                                            </div>
                                        </Tooltip>
                                        <Tooltip title={'Map'}>
                                            <div>
                                                <IconButton color={'primary'} onClick={() => onMapClick(row)} disabled={!row.Geom}>
                                                    <MapIcon />
                                                </IconButton>
                                            </div>
                                        </Tooltip>
                                        <Tooltip title={!row.IsActive ? 'Already Inactive' : 'Delete'}>
                                            <div>
                                                <IconButton
                                                    className={`${row.IsActive ? 'cr' : 'cg4'}`}
                                                    disabled={!row.IsActive}
                                                    onClick={() => deleteTransformer(row)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </div>
                                        </Tooltip>
                                        <span>{row.EPNumber}</span>
                                    </div>
                                ),
                            }, {
                                header: 'Company',
                                field: 'Company.name',
                                width: 150,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Group',
                                field: 'EPGroup',
                                width: 150,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Area',
                                field: 'Division',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Sub Area',
                                field: 'SubArea',
                                width: 175,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Pole Number',
                                field: 'PoleNumber',
                                width: 175,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Type',
                                field: 'EPType',
                                width: 150,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'CNC',
                                field: 'CNC',
                                width: 125,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Meter Type',
                                field: 'MeterType',
                                width: 150,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Account Nr',
                                field: 'AccountNumber',
                                width: 200,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Premise ID',
                                field: 'PremiseId',
                                width: 200,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Line',
                                field: 'Line',
                                width: 125,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Line Voltage',
                                field: 'LineVoltage',
                                width: 175,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: n => (<span>{!n.LineVoltage ? '' : appFunctionsService.getLineVoltageRangeLabel(n.LineVoltage)}</span>),
                            }, {
                                header: 'Circuit Breaker',
                                field: 'CircuitBreaker',
                                width: 175,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: n => !n.CircuitBreaker ? (<span></span>) : (<span>{n.CircuitBreaker} A</span>),
                            }, {
                                header: 'Transformer Rating',
                                field: 'TransformerRating',
                                width: 215,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: n => !n.TransformerRating ? (<span></span>) : (<span>{n.TransformerRating} kVA</span>),
                            }, {
                                header: 'Active?',
                                field: 'IsActive',
                                width: 215,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: n => !n.IsActive ? (<Icon className='cr'>close</Icon>) : (<Icon className='cdg'>check</Icon>),
                            }]}
                        />
                    </Card>
                    <TransformerInfo />
                    {
                        props.session?.employee.IsTransformerExport &&
                        <CSVLink
                            data={csvData}
                            headers={[
                                {
                                    key: 'EPNumber',
                                    label: 'EP Number',
                                },
                                {
                                    key: 'EPGroup',
                                    label: 'Group',
                                },
                                {
                                    key: 'Division',
                                    label: 'Area',
                                },
                                {
                                    key: 'SubArea',
                                    label: 'Sub Area',
                                },
                                {
                                    key: 'PoleNumber',
                                    label: 'Pole Number',
                                },
                                {
                                    key: 'EPType',
                                    label: 'EP Type',
                                },
                                {
                                    key: 'CNC',
                                    label: 'CNC',
                                },
                                {
                                    key: 'MeterType',
                                    label: 'Meter Type',
                                },
                                {
                                    key: 'AccountNumber',
                                    label: 'Account Number',
                                },
                                {
                                    key: 'PremiseId',
                                    label: 'Premise ID',
                                },
                                {
                                    key: 'Line',
                                    label: 'Line',
                                },
                                {
                                    key: 'LineVoltage',
                                    label: 'Line Voltage',
                                },
                                {
                                    key: 'CircuitBreaker',
                                    label: 'Circuit Breaker',
                                },
                                {
                                    key: 'TransformerRating',
                                    label: 'Transformer Rating',
                                },
                                {
                                    key: 'MeterNumbers',
                                    label: 'Meter Numbers',
                                },
                                {
                                    key: 'Latitude',
                                    label: 'Latitude',
                                },
                                {
                                    key: 'Longitude',
                                    label: 'Longitude',
                                },
                                {
                                    key: 'SecurityGuarantee',
                                    label: 'Security Guarantee',
                                },
                                {
                                    key: 'SecurityDeposit',
                                    label: 'Security Deposit',
                                },
                                {
                                    key: 'CurrentResident',
                                    label: 'Current Resident',
                                },
                                {
                                    key: 'DefaultReader',
                                    label: 'Default Reader',
                                },
                                {
                                    key: 'CreatedOn',
                                    label: 'Created On',
                                },
                                {
                                    key: 'UpdatedOn',
                                    label: 'Updated On',
                                },
                            ]}
                            filename={`${moment().valueOf()}_transformers.csv`}
                            className='dn'
                            ref={csvLink}
                            target='_blank'/>
                    }
                </div>
            </div>
           {
                !!transformerToDelete &&
                <ConfirmationDialog 
                    open={!!transformerToDelete}
                    isLoading={props.isLoadingTransformers}
                    title={'Delete Transformer'}
                    description={'Are you sure you want to inactivate the selected entry?'}
                    onAccept={handleDelete}
                    onClose={() => setTransformerToDelete(null)}
                    dialogType={'red'}
                />
           }
        </Screen>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        transformers: state.transformer.transformers,
        isLoadingTransformers: state.transformer.loading,
        session: state.auth.session,
    };
};

export default connect(
    mapStateToProps,
)(TransformerList);
